import type { ExternalLink, ResourceRole } from './access.js';
/**
 * Goal spaces: a shareable space holding goals with key results, aligned into one tree.
 * Ported from Wayfinder's goal entities/DTOs (shared/types/entities/goal.entities.ts, dto/goal.dto.ts):
 * crewSlug, ownerType/ownerId, visibility and bannerImage are gone; goals belong to a goal space.
 */

export type GoalStatus = 'not_started' | 'in_progress' | 'blocked' | 'deferred' | 'completed' | 'cancelled';

export const GOAL_STATUSES: readonly GoalStatus[] = ['not_started', 'in_progress', 'blocked', 'deferred', 'completed', 'cancelled'];

export type KeyResultType = 'boolean' | 'numeric' | 'qualitative';

export const KEY_RESULT_TYPES: readonly KeyResultType[] = ['boolean', 'numeric', 'qualitative'];

export type KeyResultStatus = 'not_started' | 'in_progress' | 'blocked' | 'deferred' | 'completed';

export const KEY_RESULT_STATUSES: readonly KeyResultStatus[] = ['not_started', 'in_progress', 'blocked', 'deferred', 'completed'];

/** 'draft' goals are visible only to their author (createdBy). */
export type GoalPublishState = 'draft' | 'published';

export { ERASED_AUTHOR } from './access.js';

/** Limits shared by client validation and server zod schemas. */
export const GOAL_LIMITS = {
    spaceTitle: 120,
    spaceDescription: 2000,
    title: 200,
    description: 10_000,
    keyResults: 25,
    keyResultTitle: 200,
    keyResultUnit: 16,
    qualitativeValue: 1000,
    goalsPerSpace: 500,
} as const;

export interface KeyResult {
    _id: string;
    title: string;
    type: KeyResultType;
    status: KeyResultStatus;
    // numeric
    targetValue?: number;
    currentValue?: number;
    /** Lower is better: the value it started from, set when the target is below the current value. */
    startValue?: number;
    /** What the number counts: "%", "days", "teams". */
    unit?: string;
    // boolean
    booleanValue?: boolean;
    // qualitative
    qualitativeValue?: string;
}

export interface GoalSpaceDto {
    shareId: string;
    title: string;
    description?: string;
    /** guid of the creator (the space's admin). */
    createdBy: string;
    createdByName: string;
    /** Banner image key; the image is at /api/images/goalSpace/<shareId>/<key> (see goalSpaceImageUrl). */
    bannerImage?: string;
    /** ISO date; archived spaces are read-only. */
    archivedAt?: string;
    createdAt: string;
    updatedAt: string;
}

export interface GoalDto {
    _id: string;
    title: string;
    /** Markdown (rendered sanitised). */
    description?: string;
    status: GoalStatus;
    /** 0-100, set by hand. */
    progress: number;
    publishState: GoalPublishState;
    /** ISO dates */
    startDate?: string;
    dueDate?: string;
    /** The same goal in another tool (a Jira epic...). */
    link?: ExternalLink;
    /** guid of the author, or ERASED_AUTHOR. */
    createdBy: string;
    createdByName: string;
    /** Who's driving this goal (any participant), optional. */
    ownerGuid?: string;
    ownerName?: string;
    /** Banner image key; the image is at /api/images/goalSpace/<shareId>/<key> (see goalSpaceImageUrl). */
    bannerImage?: string;
    keyResults: KeyResult[];

    // Alignment tree (materialized path), always within one goal space
    alignedToGoalId?: string;
    /** Root first, ending with the direct parent. */
    ancestorIds: string[];
    depth: number;
    /** Resolved ancestor stubs, root first; single-goal fetch only. */
    ancestors?: Array<{ _id: string; title: string }>;

    createdAt: string;
    updatedAt: string;
}

export interface CreateGoalSpaceDto {
    title: string;
    description?: string;
}

export interface UpdateGoalSpaceDto {
    title?: string;
    description?: string;
    /** Key from POST /api/images/goalSpace/:shareId; null removes the banner. */
    bannerImage?: string | null;
    /** Admin: true archives (read-only), false restores. */
    archived?: boolean;
}

/** Where a goal space image (banner) is served. Viewing it goes through the space's access check. */
/** The parts of a key result that say how far along it is. */
type KeyResultValues = Pick<KeyResult, 'type' | 'status' | 'targetValue' | 'currentValue' | 'startValue' | 'booleanValue'>;

/** A number to bring down ("p95 under 300 ms", "days to first value: 14 → 7"): its target is below where it started. */
export function isLowerBetter(kr: Pick<KeyResult, 'type' | 'targetValue' | 'startValue'>): boolean {
    return kr.type === 'numeric' && kr.startValue != null && kr.targetValue != null && kr.targetValue < kr.startValue;
}

/**
 * Where a numeric key result counts from. A target set below the current value means lower is better: the current
 * value becomes the start (kept from then on), and a start the target no longer sits below is dropped. `previous` is
 * the saved key result being edited: when its target didn't change, a current value past the target is a count-up
 * result that overshot (done), not one counting down.
 */
export function startValueFor(
    kr: Pick<KeyResult, 'targetValue' | 'currentValue' | 'startValue'>,
    previous?: Pick<KeyResult, 'targetValue'>,
): number | undefined {
    if (kr.targetValue == null) return undefined;
    if (kr.startValue != null) return kr.targetValue < kr.startValue ? kr.startValue : undefined;
    if (previous && previous.targetValue === kr.targetValue) return undefined;
    return kr.currentValue != null && kr.targetValue < kr.currentValue ? kr.currentValue : undefined;
}

const clampPercent = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** Progress of one key result, 0-100. Qualitative results count by status only. */
export function keyResultProgress<K extends KeyResultValues>(kr: K): number {
    if (kr.status === 'completed') return 100;
    if (kr.type === 'boolean') return kr.booleanValue ? 100 : 0;
    if (kr.type === 'numeric') {
        // Lower is better: how far it has come down from its start towards the target
        if (isLowerBetter(kr)) return clampPercent(((kr.startValue! - (kr.currentValue ?? kr.startValue!)) / (kr.startValue! - kr.targetValue!)) * 100);
        if (!kr.targetValue) return 0;
        return clampPercent(((kr.currentValue ?? 0) / kr.targetValue) * 100);
    }
    return kr.status === 'in_progress' ? 50 : 0;
}

/** A goal with key results is as far along as their average; undefined when it has none (progress is set by hand). */
export function progressFromKeyResults<K extends KeyResultValues>(keyResults: readonly K[]): number | undefined {
    if (!keyResults.length) return undefined;
    return Math.round(keyResults.reduce((sum, kr) => sum + keyResultProgress(kr), 0) / keyResults.length);
}

/**
 * The status a goal or key result should move to when its progress changes, if any: starting it (above 0) makes
 * a not-started one in progress, reaching 100 completes an open one, and dropping below 100 reopens a completed
 * one. Blocked, deferred and cancelled are deliberate choices and stay put.
 */
export function statusForProgress<S extends GoalStatus>(status: S, progress: number): S | 'in_progress' | 'completed' | undefined {
    if (progress >= 100 && (status === 'not_started' || status === 'in_progress')) return 'completed';
    if (progress > 0 && progress < 100 && status === 'not_started') return 'in_progress';
    if (progress < 100 && status === 'completed') return 'in_progress';
    return undefined;
}

/** The status that progress alone implies: not started at 0, completed at 100, in progress between. */
export function statusFromProgress(progress: number): 'not_started' | 'in_progress' | 'completed' {
    return progress >= 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started';
}

/** A measurable key result's status follows its value. */
export function keyResultStatusFor<K extends KeyResultValues>(kr: K): KeyResultStatus {
    // Qualitative ones, and numbers without a target, are set by hand
    if (kr.type === 'qualitative' || (kr.type === 'numeric' && !kr.targetValue && !isLowerBetter(kr))) return kr.status;
    const measured = kr.type === 'boolean'
        ? (kr.booleanValue ? 100 : 0)
        : keyResultProgress({ ...kr, status: 'in_progress' });
    return statusForProgress(kr.status, measured) ?? kr.status;
}

export function goalSpaceImageUrl(shareId: string, key: string): string {
    return `/api/images/goalSpace/${shareId}/${key}`;
}

export interface KeyResultInput {
    /** Present when editing an existing key result. */
    _id?: string;
    title: string;
    type: KeyResultType;
    status?: KeyResultStatus;
    targetValue?: number;
    currentValue?: number;
    /** Sent back as received, so a lower-is-better result keeps its start. */
    startValue?: number;
    unit?: string;
    booleanValue?: boolean;
    qualitativeValue?: string;
}

export interface CreateGoalDto {
    title: string;
    description?: string;
    status?: GoalStatus;
    progress?: number;
    /** Defaults to 'published'. */
    publishState?: GoalPublishState;
    startDate?: string;
    dueDate?: string;
    link?: ExternalLink;
    alignedToGoalId?: string;
    ownerGuid?: string;
    /** Key from POST /api/images/goalSpace/:shareId. */
    bannerImage?: string;
    keyResults?: KeyResultInput[];
}

export interface UpdateGoalDto {
    title?: string;
    /** '' or null clears. */
    description?: string | null;
    status?: GoalStatus;
    progress?: number;
    publishState?: GoalPublishState;
    /** null clears. */
    startDate?: string | null;
    dueDate?: string | null;
    /** null removes it. */
    link?: ExternalLink | null;
    /** null = top-level goal. */
    alignedToGoalId?: string | null;
    /** null = nobody. */
    ownerGuid?: string | null;
    /** Key from POST /api/images/goalSpace/:shareId; null removes the banner. */
    bannerImage?: string | null;
    /** Full replacement of the key results array. */
    keyResults?: KeyResultInput[];
}

/** Minimal goal shape for the alignment picker (published goals only). */
export interface GoalAlignmentOption {
    _id: string;
    title: string;
    status: GoalStatus;
    startDate?: string;
    dueDate?: string;
    alignedToGoalId?: string;
}

/** Live events published on the goal space channel. */
export const GOAL_SPACE_EVENTS = {
    spaceUpdated: 'space_updated',
    goalCreated: 'goal_created',
    goalUpdated: 'goal_updated',
    goalDeleted: 'goal_deleted',
} as const;

/** goal_deleted payload: children of the deleted goal move up to its parent. */
export interface GoalDeletedEvent {
    _id: string;
    alignedToGoalId?: string;
}

// ── Who may change a goal (server enforces, client mirrors) ────

export interface GoalViewer {
    /** null for anonymous viewers. */
    guid: string | null;
    role: ResourceRole;
}

type GoalAuthorship = { publishState: GoalPublishState; createdBy: string; ownerGuid?: string | null };

/**
 * Who may edit a goal:
 * - drafts: the author only
 * - the space admin (owner): any published goal
 * - contributors: goals they created, and goals they drive (ownerGuid)
 * Anonymous viewers never edit.
 */
export function canEditGoal(goal: GoalAuthorship, viewer: GoalViewer): boolean {
    if (!viewer.guid || viewer.role === 'viewer') return false;
    if (goal.publishState === 'draft') return goal.createdBy === viewer.guid;
    if (viewer.role === 'admin') return true;
    return goal.createdBy === viewer.guid || (!!goal.ownerGuid && goal.ownerGuid === viewer.guid);
}

/** Delete is narrower than edit: the author, or the admin for published goals. Drivers can't delete. */
export function canDeleteGoal(goal: Omit<GoalAuthorship, 'ownerGuid'>, viewer: GoalViewer): boolean {
    if (!viewer.guid || viewer.role === 'viewer') return false;
    if (goal.createdBy === viewer.guid) return true;
    return goal.publishState !== 'draft' && viewer.role === 'admin';
}
